import { logger } from '@vagabondinternet/logging';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { lobbyFailure, lobbyFailureMessage } from '../../api/games';
import { displayNameDetour } from '../../games/display-name-detour';
import { useSession } from '../../session';

/**
 * Handles a failed lobby request: a player with no display name goes to /profile to choose one and then back to
 * `returnPath`; an expired session goes to sign-in; anything else resolves to the message the player reads.
 */
export const useLobbyFailure = () => {
  const navigate = useNavigate();
  const { goToSignIn } = useSession();

  return useCallback(
    (error: unknown, returnPath: string): string | undefined => {
      const failure = lobbyFailure(error);
      if (failure.kind === 'displayNameRequired') {
        navigate(displayNameDetour(returnPath), { replace: true });
        return undefined;
      }
      if (failure.kind === 'unauthenticated') {
        goToSignIn();
        return undefined;
      }
      if (failure.kind === 'failed') {
        logger.warn({ message: 'A lobby request failed', error: (error as Error).message });
      }
      return lobbyFailureMessage(failure);
    },
    [navigate, goToSignIn],
  );
};
